import { carryForward, lookupRatesSync, todayIso } from './fxRates';

/**
 * Date → display-currency rate, memoised.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * The app stores money in USD and used to convert to the display currency at
 * ONE rate — today's spot — at render time. That is correct for what something
 * is worth NOW, and wrong for what it COST: a purchase made in 2023 cost what it
 * cost, at 2023's rate, and re-converting it at today's rate reports a number the
 * user never paid.
 *
 * So cost-like figures are converted PER TRANSACTION at that transaction's own
 * date. This table is the lookup that makes that affordable.
 *
 * ── Why memoised ─────────────────────────────────────────────────────────────
 *
 * `computeHoldings` runs on every price tick, and `usePortfolioHistory` calls it
 * once per day of history — so a naive per-transaction rate lookup (each one a
 * roll-back loop over up to 11 candidate dates) multiplies out fast. A `Map`
 * keyed by date collapses all of that to one lookup per distinct date, and
 * distinct dates are bounded by the number of days the user has traded on.
 *
 * Build one table per (currency, spot) pair and reuse it: the caller is expected
 * to hold it in a `useMemo`. Populating it is lazy, so a table costs nothing
 * until something asks it for a date.
 */
export interface DisplayRateTable {
  /** The currency this table converts INTO. */
  readonly currency: string;
  /** Units of `currency` per 1 USD, on `date` (rolled back to a business day). */
  perUsdOn(date: string): number;
  /**
   * True once any lookup has fallen back to today's spot because the date could
   * not be resolved. Read it AFTER the figures are computed, not before.
   */
  hasEstimates(): boolean;
}

/**
 * A table that converts nothing — every rate is exactly 1.
 *
 * Used when no currency context is available and by `computeHoldings`' default,
 * so the display figures degrade to the USD figures rather than to zero. That is
 * what keeps every existing caller and test behaving exactly as before.
 */
export const IDENTITY_RATES: DisplayRateTable = {
  currency: 'USD',
  perUsdOn: () => 1,
  hasEstimates: () => false,
};

/**
 * Units of `currency` per USD on each date, memoised.
 *
 * Every lookup is strict at the RBA bundle's end (see lookupRatesSync), and a
 * past date nothing resolves CARRIES FORWARD the most recent earlier known rate
 * (carryForward), flagged via hasEstimates, rather than jumping to today's spot.
 * The CGT engine uses the same rule, so holdings costs, the Tax Harvest
 * Estimator, the charts and the CGT report all agree on a date's rate.
 */
export function buildDisplayRateTable(
  currency: string,
  spotPerUsd: number
): DisplayRateTable {
  const code = (currency || 'USD').toUpperCase();
  const spot = isFinite(spotPerUsd) && spotPerUsd > 0 ? spotPerUsd : 1;

  // USD needs no conversion at all, and a rate of exactly 1 for every date is
  // both correct and the cheapest possible answer.
  if (code === 'USD') return IDENTITY_RATES;

  const cache = new Map<string, number>();
  let estimated = false;
  const today = todayIso();

  /** Units of `code` per USD actually published for `date` (with roll-back), or null. */
  const published = (date: string): number | null => {
    const resolved = lookupRatesSync(code, date);
    return resolved && isFinite(resolved.fxRateUsd) && resolved.fxRateUsd > 0
      ? 1 / resolved.fxRateUsd
      : null;
  };

  return {
    currency: code,
    perUsdOn(date: string): number {
      const cached = cache.get(date);
      if (cached !== undefined) return cached;

      // `fxRateUsd` is USD per 1 unit of `code`, so its reciprocal is what we
      // want: units of `code` per 1 USD. This is the module's one inversion.
      let perUsd: number;
      const exact = published(date);
      const carried =
        exact === null && date < today ? carryForward(date, (d) => cache.get(d) ?? published(d))?.value ?? null : null;
      if (exact !== null) {
        perUsd = exact;
      } else if (carried !== null) {
        // Nothing published for this date: the last known rate, flagged as an
        // estimate exactly as a spot fallback is.
        perUsd = carried;
        estimated = true;
      } else {
        // No published rate for this date. Today's spot is the only number on
        // hand; flag it rather than let an approximation pass as exact.
        perUsd = spot;
        // Two dates are NOT evidence of anything wrong:
        //   - today itself, and anything later: the live spot IS the correct rate
        //     for today, so using it is the right answer, not an approximation.
        //     (Class B value figures ask for today's rate on every call.)
        //   - a future date, which has no published rate anywhere and never will.
        if (date < today) estimated = true;
      }

      cache.set(date, perUsd);
      return perUsd;
    },
    hasEstimates: () => estimated,
  };
}
